import { useEffect, useRef, useState } from 'react'
import { Award, AlertCircle, Upload, CheckCircle, Plus, X, ChevronLeft } from 'lucide-react'
import api from '../../services/api'

const ORANGE = '#FF9500'
const RED    = '#FF3B5C'
const GREEN  = '#00D4AA'
const DIM    = 'rgba(255,255,255,0.3)'

const EXPIRY_META: Record<string, { color: string; label: string }> = {
  expired:      { color: RED,    label: 'Expired' },
  expiring_soon:{ color: ORANGE, label: 'Expiring' },
  valid:        { color: GREEN,  label: 'Valid' },
  no_expiry:    { color: DIM,    label: 'No expiry' },
}

const FILTERS = [
  ['expiring_soon', 'Expiring'],
  ['expired',       'Expired'],
  ['valid',         'Valid'],
  ['all',           'All'],
]

const emptyForm = {
  worker_id: '', competency_type: 'licence', competency_name: '',
  licence_class: '', licence_number: '', issuing_authority: '', issue_date: '', expiry_date: '',
  ai_extracted: false, document_filepath: '',
}

export default function MobileLicences() {
  const [data, setData] = useState<any[]>([])
  const [workers, setWorkers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('expiring_soon')
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [aiNotice, setAiNotice] = useState('')
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter !== 'all') params.set('expiry_status', filter)
      const { data: res } = await api.get(`/competencies?${params}&limit=100`)
      setData(res.data || [])
    } catch {}
    setLoading(false)
  }

  useEffect(() => {
    api.get('/workforce').catch(() => ({ data: { data: [] } })).then((r: any) => setWorkers(Array.isArray(r.data?.data) ? r.data.data : []))
  }, [])

  useEffect(() => { load() }, [filter])

  const extractLicence = async (file: File) => {
    setExtracting(true); setAiNotice('')
    try {
      const fd = new FormData(); fd.append('file', file)
      const { data: result } = await api.post('/competencies/upload-licence', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const updates: Partial<typeof emptyForm> = { ai_extracted: true }
      if (result.competency_name) updates.competency_name = result.competency_name
      if (result.licence_class) updates.licence_class = result.licence_class
      if (result.licence_number) updates.licence_number = result.licence_number
      if (result.issuing_authority) updates.issuing_authority = result.issuing_authority
      if (result.issue_date) updates.issue_date = result.issue_date
      if (result.expiry_date) updates.expiry_date = result.expiry_date
      if (result.document_filepath) updates.document_filepath = result.document_filepath
      setForm(f => ({ ...f, ...updates }))
      setAiNotice(result.holder_name ? `Extracted for ${result.holder_name} — select worker below` : 'Fields auto-filled — verify before saving')
    } catch {
      setAiNotice('Extraction failed — fill in manually')
    }
    setExtracting(false)
  }

  const handleSave = async () => {
    if (!form.worker_id || !form.competency_name) { setError('Worker and name required'); return }
    setSaving(true); setError('')
    try {
      await api.post('/competencies', {
        worker_id: form.worker_id,
        competency_type: form.competency_type,
        competency_name: form.competency_name,
        licence_class: form.licence_class || undefined,
        licence_number: form.licence_number || undefined,
        issuing_authority: form.issuing_authority || undefined,
        issue_date: form.issue_date || undefined,
        expiry_date: form.expiry_date || undefined,
        ai_extracted: form.ai_extracted,
        document_filepath: form.document_filepath || undefined,
      })
      setShowAdd(false); setForm(emptyForm); setAiNotice(''); load()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  const daysUntil = (date: string) => Math.ceil((new Date(date).getTime() - Date.now()) / 86400000)

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%', paddingBottom: 96 }}>
      {/* Sticky header */}
      <div
        className="sticky top-0 z-10"
        style={{ background: 'var(--bg)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px 12px' }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#FFFFFF', margin: '0 0 12px' }}>Licences</h1>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {FILTERS.map(([val, label]) => {
            const active = filter === val
            return (
              <button
                key={val}
                onClick={() => setFilter(val)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                  border: 'none',
                  cursor: 'pointer',
                  background: active ? ORANGE : 'rgba(255,255,255,0.08)',
                  color: active ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                  transition: 'all 150ms',
                }}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* List */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '64px 0', color: DIM }}>Loading…</div>
        ) : data.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', gap: 12 }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: `${GREEN}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Award size={26} color={GREEN} />
            </div>
            <p style={{ fontWeight: 600, fontSize: 15, color: '#FFFFFF', margin: 0 }}>
              {filter === 'expiring_soon' ? 'No licences expiring soon' : filter === 'expired' ? 'No expired licences' : 'No records'}
            </p>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0, textAlign: 'center' }}>
              {filter === 'all' ? 'Tap + to add a licence' : 'Credentials are up to date'}
            </p>
          </div>
        ) : (
          data.map(lic => {
            const meta = EXPIRY_META[lic.expiry_status] || EXPIRY_META.no_expiry
            const days = lic.expiry_date ? daysUntil(lic.expiry_date) : null
            return (
              <div
                key={lic.id}
                style={{
                  background: 'var(--white)',
                  borderRadius: 18,
                  borderLeft: `3px solid ${meta.color}`,
                  padding: '14px 14px 14px 16px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#FFFFFF' }}>{lic.worker_name}</span>
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, fontWeight: 700, background: `${meta.color}22`, color: meta.color }}>
                        {meta.label}
                      </span>
                      {lic.ai_extracted === 1 && (
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700, background: '#BF5AF220', color: '#BF5AF2' }}>AI</span>
                      )}
                    </div>
                    <p style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.6)', margin: '0 0 3px' }}>{lic.competency_name}</p>
                    {lic.licence_number && (
                      <p style={{ fontSize: 11, fontFamily: 'monospace', color: DIM, margin: 0 }}>{lic.licence_number}</p>
                    )}
                    {lic.issuing_authority && (
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', margin: '2px 0 0' }}>{lic.issuing_authority}</p>
                    )}
                  </div>
                  {days !== null && (
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{ fontWeight: 900, fontSize: 22, color: meta.color, lineHeight: 1, margin: 0, letterSpacing: '-0.02em' }}>
                        {Math.abs(days)}d
                      </p>
                      <p style={{ fontSize: 10, color: meta.color, margin: '3px 0 0' }}>
                        {days < 0 ? 'expired' : 'left'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => { setShowAdd(true); setForm(emptyForm); setAiNotice(''); setError('') }}
        style={{
          position: 'fixed',
          bottom: 'calc(64px + env(safe-area-inset-bottom) + 16px)',
          right: 20,
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: ORANGE,
          boxShadow: `0 0 24px ${ORANGE}55`,
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        className="active:scale-95 transition-transform"
      >
        <Plus size={24} color="white" />
      </button>

      {/* Add modal — full screen */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
          {/* Modal header */}
          <div style={{ background: '#0F1823', padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
            <button
              onClick={() => setShowAdd(false)}
              style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
            >
              <ChevronLeft size={18} color="rgba(255,255,255,0.7)" />
            </button>
            <h2 style={{ fontWeight: 700, fontSize: 16, color: '#FFFFFF', margin: 0 }}>Add Licence / Credential</h2>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* AI Upload */}
            <div style={{ background: 'var(--white)', borderRadius: 18, padding: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 12px' }}>AI Extraction</p>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) extractLicence(f); if (fileRef.current) fileRef.current.value = '' }}
              />
              <button
                type="button"
                disabled={extracting}
                onClick={() => fileRef.current?.click()}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontWeight: 600,
                  fontSize: 13,
                  border: `2px dashed ${form.ai_extracted ? GREEN : 'rgba(255,255,255,0.15)'}`,
                  background: form.ai_extracted ? `${GREEN}15` : 'rgba(255,255,255,0.04)',
                  color: extracting ? DIM : form.ai_extracted ? GREEN : 'rgba(255,255,255,0.6)',
                  cursor: extracting ? 'default' : 'pointer',
                }}
              >
                {extracting
                  ? <><span style={{ width: 16, height: 16, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> Scanning…</>
                  : form.ai_extracted
                    ? <><CheckCircle size={16} /> AI extracted — verify below</>
                    : <><Upload size={16} /> Upload licence to auto-fill</>
                }
              </button>
              {aiNotice && (
                <p style={{ fontSize: 11, marginTop: 8, color: aiNotice.includes('failed') ? RED : 'rgba(255,255,255,0.45)' }}>
                  {aiNotice}
                </p>
              )}
            </div>

            {/* Form fields */}
            <div style={{ background: 'var(--white)', borderRadius: 18, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Worker *</label>
                <select value={form.worker_id} onChange={e => setForm(f => ({ ...f, worker_id: e.target.value }))} style={{ fontSize: 16 }}>
                  <option value="">— Select worker —</option>
                  {workers.map((u: any) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Category</label>
                <select value={form.competency_type} onChange={e => setForm(f => ({ ...f, competency_type: e.target.value }))} style={{ fontSize: 16 }}>
                  <option value="licence">Licence</option>
                  <option value="certificate">Certificate</option>
                  <option value="induction">Induction</option>
                  <option value="training">Training</option>
                  <option value="whs_training">WHS Training</option>
                  <option value="first_aid">First Aid</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Name *</label>
                <input value={form.competency_name} onChange={e => setForm(f => ({ ...f, competency_name: e.target.value }))} placeholder="e.g. High Risk Work Licence" style={{ fontSize: 16 }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Licence No.</label>
                  <input value={form.licence_number} onChange={e => setForm(f => ({ ...f, licence_number: e.target.value }))} placeholder="WHS-12345" style={{ fontSize: 16 }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Class</label>
                  <input value={form.licence_class} onChange={e => setForm(f => ({ ...f, licence_class: e.target.value }))} placeholder="e.g. LF" style={{ fontSize: 16 }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Issuing Authority</label>
                <input value={form.issuing_authority} onChange={e => setForm(f => ({ ...f, issuing_authority: e.target.value }))} placeholder="e.g. SafeWork NSW" style={{ fontSize: 16 }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Issue Date</label>
                  <input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Expiry Date</label>
                  <input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
                </div>
              </div>
            </div>

            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 14, background: `${RED}18`, color: RED, fontSize: 13 }}>
                <AlertCircle size={15} /> {error}
              </div>
            )}
          </div>

          {/* Save footer */}
          <div style={{ padding: '12px 16px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid rgba(255,255,255,0.07)', background: '#0F1823', flexShrink: 0 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: 16,
                fontSize: 15,
                fontWeight: 700,
                color: '#FFFFFF',
                background: saving ? 'rgba(255,255,255,0.15)' : ORANGE,
                border: 'none',
                cursor: saving ? 'default' : 'pointer',
                boxShadow: saving ? 'none' : `0 0 20px ${ORANGE}40`,
              }}
              className="active:scale-[0.98] transition-transform"
            >
              {saving ? 'Saving…' : 'Save Record'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
